import type {
  AppleChip,
  BenchmarkProfile,
  GpuProfile,
  MachineProfile,
  ModelProfile,
  Quantization,
  RuntimeProfile,
} from './types.js';

export const QUANT_BITS: Record<Quantization, number> = {
  fp16: 16,
  q8_0: 8.5,
  q6_k: 6.6,
  q5_k_m: 5.5,
  q4_k_m: 4.8,
  q3_k_m: 3.9,
  q2_k: 3.1,
};

export const QUANT_QUALITY_PENALTY: Record<Quantization, number> = {
  fp16: 0,
  q8_0: 0.5,
  q6_k: 1.2,
  q5_k_m: 2.0,
  q4_k_m: 3.5,
  q3_k_m: 6.0,
  q2_k: 10.0,
};

export const QUANT_LABEL: Record<Quantization, string> = {
  fp16: 'FP16',
  q8_0: 'Q8_0',
  q6_k: 'Q6_K',
  q5_k_m: 'Q5_K_M',
  q4_k_m: 'Q4_K_M',
  q3_k_m: 'Q3_K_M',
  q2_k: 'Q2_K',
};

export const APPLE_CHIPS: AppleChip[] = [
  { id: 'm1', label: 'Apple M1', generation: 'm1', tier: 'base', gpuCores: 8, memoryBandwidthGbps: 68 },
  {
    id: 'm1-pro',
    label: 'Apple M1 Pro',
    generation: 'm1',
    tier: 'pro',
    gpuCores: 16,
    memoryBandwidthGbps: 200,
  },
  {
    id: 'm1-max',
    label: 'Apple M1 Max',
    generation: 'm1',
    tier: 'max',
    gpuCores: 32,
    memoryBandwidthGbps: 400,
  },
  {
    id: 'm1-ultra',
    label: 'Apple M1 Ultra',
    generation: 'm1',
    tier: 'ultra',
    gpuCores: 64,
    memoryBandwidthGbps: 800,
  },
  { id: 'm2', label: 'Apple M2', generation: 'm2', tier: 'base', gpuCores: 10, memoryBandwidthGbps: 100 },
  {
    id: 'm2-pro',
    label: 'Apple M2 Pro',
    generation: 'm2',
    tier: 'pro',
    gpuCores: 19,
    memoryBandwidthGbps: 200,
  },
  {
    id: 'm2-max',
    label: 'Apple M2 Max',
    generation: 'm2',
    tier: 'max',
    gpuCores: 38,
    memoryBandwidthGbps: 400,
  },
  {
    id: 'm2-ultra',
    label: 'Apple M2 Ultra',
    generation: 'm2',
    tier: 'ultra',
    gpuCores: 76,
    memoryBandwidthGbps: 800,
  },
  { id: 'm3', label: 'Apple M3', generation: 'm3', tier: 'base', gpuCores: 10, memoryBandwidthGbps: 100 },
  {
    id: 'm3-pro',
    label: 'Apple M3 Pro',
    generation: 'm3',
    tier: 'pro',
    gpuCores: 18,
    memoryBandwidthGbps: 150,
  },
  {
    id: 'm3-max',
    label: 'Apple M3 Max',
    generation: 'm3',
    tier: 'max',
    gpuCores: 40,
    memoryBandwidthGbps: 400,
  },
  { id: 'm4', label: 'Apple M4', generation: 'm4', tier: 'base', gpuCores: 10, memoryBandwidthGbps: 120 },
  {
    id: 'm4-pro',
    label: 'Apple M4 Pro',
    generation: 'm4',
    tier: 'pro',
    gpuCores: 20,
    memoryBandwidthGbps: 273,
  },
  {
    id: 'm4-max',
    label: 'Apple M4 Max',
    generation: 'm4',
    tier: 'max',
    gpuCores: 40,
    memoryBandwidthGbps: 546,
  },
];

export const GPUS: GpuProfile[] = [
  {
    id: 'rtx-4090',
    label: 'NVIDIA RTX 4090',
    vendor: 'nvidia',
    vramGb: 24,
    memoryBandwidthGbps: 1008,
    computeTierTps: 120,
  },
  {
    id: 'rtx-4080',
    label: 'NVIDIA RTX 4080',
    vendor: 'nvidia',
    vramGb: 16,
    memoryBandwidthGbps: 717,
    computeTierTps: 90,
  },
  {
    id: 'rtx-4070-ti',
    label: 'NVIDIA RTX 4070 Ti',
    vendor: 'nvidia',
    vramGb: 12,
    memoryBandwidthGbps: 504,
    computeTierTps: 70,
  },
  {
    id: 'rtx-4070',
    label: 'NVIDIA RTX 4070',
    vendor: 'nvidia',
    vramGb: 12,
    memoryBandwidthGbps: 504,
    computeTierTps: 60,
  },
  {
    id: 'rtx-4060-ti-16',
    label: 'NVIDIA RTX 4060 Ti 16GB',
    vendor: 'nvidia',
    vramGb: 16,
    memoryBandwidthGbps: 288,
    computeTierTps: 45,
  },
  {
    id: 'rtx-4060',
    label: 'NVIDIA RTX 4060',
    vendor: 'nvidia',
    vramGb: 8,
    memoryBandwidthGbps: 272,
    computeTierTps: 42,
  },
  {
    id: 'rtx-3090',
    label: 'NVIDIA RTX 3090',
    vendor: 'nvidia',
    vramGb: 24,
    memoryBandwidthGbps: 936,
    computeTierTps: 85,
  },
  {
    id: 'rtx-3080-12',
    label: 'NVIDIA RTX 3080 12GB',
    vendor: 'nvidia',
    vramGb: 12,
    memoryBandwidthGbps: 912,
    computeTierTps: 72,
  },
  {
    id: 'rtx-3070',
    label: 'NVIDIA RTX 3070',
    vendor: 'nvidia',
    vramGb: 8,
    memoryBandwidthGbps: 448,
    computeTierTps: 48,
  },
  {
    id: 'rtx-3060-12',
    label: 'NVIDIA RTX 3060 12GB',
    vendor: 'nvidia',
    vramGb: 12,
    memoryBandwidthGbps: 360,
    computeTierTps: 38,
  },
  {
    id: 'rtx-5090',
    label: 'NVIDIA RTX 5090',
    vendor: 'nvidia',
    vramGb: 32,
    memoryBandwidthGbps: 1792,
    computeTierTps: 180,
  },
  {
    id: 'rx-7900-xtx',
    label: 'AMD Radeon 7900 XTX',
    vendor: 'amd',
    vramGb: 24,
    memoryBandwidthGbps: 960,
    computeTierTps: 80,
  },
  {
    id: 'rx-7900-xt',
    label: 'AMD Radeon 7900 XT',
    vendor: 'amd',
    vramGb: 20,
    memoryBandwidthGbps: 800,
    computeTierTps: 68,
  },
  {
    id: 'rx-7800-xt',
    label: 'AMD Radeon 7800 XT',
    vendor: 'amd',
    vramGb: 16,
    memoryBandwidthGbps: 624,
    computeTierTps: 55,
  },
  {
    id: 'arc-a770',
    label: 'Intel Arc A770',
    vendor: 'intel',
    vramGb: 16,
    memoryBandwidthGbps: 560,
    computeTierTps: 28,
  },
];

export const MODELS: [ModelProfile, ...ModelProfile[]] = [
  {
    id: 'qwen-2.5-coder-32b',
    displayName: 'Qwen 2.5 Coder 32B',
    family: 'qwen',
    paramsB: 32,
    contextMax: 131072,
    useCase: 'coder',
    codingTier: 'S',
    baseQualityScore: 89,
    license: 'Apache 2.0',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m'],
    ollamaTag: 'qwen2.5-coder:32b',
  },
  {
    id: 'qwen-2.5-coder-14b',
    displayName: 'Qwen 2.5 Coder 14B',
    family: 'qwen',
    paramsB: 14,
    contextMax: 131072,
    useCase: 'coder',
    codingTier: 'A',
    baseQualityScore: 83,
    license: 'Apache 2.0',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m'],
    ollamaTag: 'qwen2.5-coder:14b',
  },
  {
    id: 'qwen-2.5-coder-7b',
    displayName: 'Qwen 2.5 Coder 7B',
    family: 'qwen',
    paramsB: 7,
    contextMax: 131072,
    useCase: 'coder',
    codingTier: 'A',
    baseQualityScore: 76,
    license: 'Apache 2.0',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m'],
    ollamaTag: 'qwen2.5-coder:7b',
  },
  {
    id: 'qwen-2.5-coder-3b',
    displayName: 'Qwen 2.5 Coder 3B',
    family: 'qwen',
    paramsB: 3,
    contextMax: 32768,
    useCase: 'coder',
    codingTier: 'B',
    baseQualityScore: 68,
    license: 'Apache 2.0',
    quantizations: ['fp16', 'q8_0', 'q5_k_m', 'q4_k_m'],
    ollamaTag: 'qwen2.5-coder:3b',
  },
  {
    id: 'qwen-2.5-coder-1.5b',
    displayName: 'Qwen 2.5 Coder 1.5B',
    family: 'qwen',
    paramsB: 1.5,
    contextMax: 32768,
    useCase: 'coder',
    codingTier: 'C',
    baseQualityScore: 58,
    license: 'Apache 2.0',
    quantizations: ['fp16', 'q8_0', 'q4_k_m'],
    ollamaTag: 'qwen2.5-coder:1.5b',
  },
  {
    id: 'deepseek-coder-v2-16b',
    displayName: 'DeepSeek Coder V2 Lite 16B',
    family: 'deepseek',
    paramsB: 16,
    contextMax: 163840,
    useCase: 'coder',
    codingTier: 'A',
    baseQualityScore: 82,
    license: 'DeepSeek',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m'],
    ollamaTag: 'deepseek-coder-v2:16b',
  },
  {
    id: 'codestral-22b',
    displayName: 'Codestral 22B',
    family: 'mistral',
    paramsB: 22,
    contextMax: 32768,
    useCase: 'coder',
    codingTier: 'A',
    baseQualityScore: 81,
    license: 'Mistral Research',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m'],
    ollamaTag: 'codestral:22b',
  },
  {
    id: 'code-llama-34b',
    displayName: 'Code Llama 34B',
    family: 'llama',
    paramsB: 34,
    contextMax: 16384,
    useCase: 'coder',
    codingTier: 'B',
    baseQualityScore: 72,
    license: 'Llama 2',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m'],
    ollamaTag: 'codellama:34b',
  },
  {
    id: 'llama-3.3-70b',
    displayName: 'Llama 3.3 70B',
    family: 'llama',
    paramsB: 70,
    contextMax: 131072,
    useCase: 'reasoner',
    codingTier: 'A',
    baseQualityScore: 85,
    license: 'Llama 3',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m', 'q3_k_m'],
    ollamaTag: 'llama3.3:70b',
  },
  {
    id: 'llama-3.2-3b',
    displayName: 'Llama 3.2 3B',
    family: 'llama',
    paramsB: 3,
    contextMax: 131072,
    useCase: 'chat',
    codingTier: 'C',
    baseQualityScore: 62,
    license: 'Llama 3',
    quantizations: ['fp16', 'q8_0', 'q5_k_m', 'q4_k_m'],
    ollamaTag: 'llama3.2:3b',
  },
  {
    id: 'phi-4-14b',
    displayName: 'Phi-4 14B',
    family: 'phi',
    paramsB: 14,
    contextMax: 16384,
    useCase: 'reasoner',
    codingTier: 'B',
    baseQualityScore: 77,
    license: 'MIT',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m'],
    ollamaTag: 'phi4:14b',
  },
  {
    id: 'gemma-2-9b',
    displayName: 'Gemma 2 9B',
    family: 'gemma',
    paramsB: 9,
    contextMax: 8192,
    useCase: 'chat',
    codingTier: 'C',
    baseQualityScore: 68,
    license: 'Gemma',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m'],
    ollamaTag: 'gemma2:9b',
  },
  {
    id: 'starcoder-2-15b',
    displayName: 'StarCoder 2 15B',
    family: 'starcoder',
    paramsB: 15,
    contextMax: 16384,
    useCase: 'coder',
    codingTier: 'B',
    baseQualityScore: 71,
    license: 'BigCode OpenRAIL',
    quantizations: ['fp16', 'q8_0', 'q6_k', 'q5_k_m', 'q4_k_m'],
    ollamaTag: 'starcoder2:15b',
  },
];

export const RUNTIMES: RuntimeProfile[] = [
  {
    id: 'ollama',
    label: 'Ollama',
    accelerators: ['apple-silicon', 'cuda', 'rocm', 'cpu'],
    strengths: ['one-command install', 'huge model library', 'great Mac + Linux support'],
    notes: 'Best default for almost every machine. Beginner-safe.',
    beginnerFriendly: true,
  },
  {
    id: 'lm-studio',
    label: 'LM Studio',
    accelerators: ['apple-silicon', 'cuda', 'cpu'],
    strengths: ['polished UI', 'OpenAI-compatible server', 'easy quant picker'],
    notes: 'Best if you prefer a GUI. Slightly slower than raw llama.cpp at max speed.',
    beginnerFriendly: true,
  },
  {
    id: 'llama-cpp',
    label: 'llama.cpp',
    accelerators: ['apple-silicon', 'cuda', 'rocm', 'cpu'],
    strengths: ['fastest inference engine', 'most granular control', 'GGUF first'],
    notes: 'Best for power users tuning throughput and memory.',
    beginnerFriendly: false,
  },
  {
    id: 'mlx',
    label: 'MLX',
    accelerators: ['apple-silicon'],
    strengths: ['Apple Silicon native', 'lowest memory footprint on M-series'],
    notes: 'Best on M3/M4 Max + Ultra. Fewer model formats than GGUF.',
    beginnerFriendly: false,
  },
  {
    id: 'vllm',
    label: 'vLLM',
    accelerators: ['cuda'],
    strengths: ['server-grade throughput', 'continuous batching'],
    notes: 'Best for team or production CUDA hosts, not laptops.',
    beginnerFriendly: false,
  },
];

export const MACHINE_PRESETS: [MachineProfile, ...MachineProfile[]] = [
  {
    id: 'macbook-air-m2-16',
    nickname: 'MacBook Air M2 16GB',
    os: 'macos',
    cpuLabel: 'Apple M2',
    accel: 'apple-silicon',
    appleChipId: 'm2',
    ramGb: 16,
    vramGb: 14,
    storageFreeGb: 120,
    installedRuntimes: ['ollama'],
    preferredRuntime: 'ollama',
  },
  {
    id: 'macbook-pro-m3-max',
    nickname: 'MacBook Pro M3 Max 36GB',
    os: 'macos',
    cpuLabel: 'Apple M3 Max',
    accel: 'apple-silicon',
    appleChipId: 'm3-max',
    ramGb: 36,
    vramGb: 32,
    storageFreeGb: 500,
    installedRuntimes: ['ollama', 'lm-studio', 'mlx'],
    preferredRuntime: 'mlx',
  },
  {
    id: 'macbook-pro-m4-max-64',
    nickname: 'MacBook Pro M4 Max 64GB',
    os: 'macos',
    cpuLabel: 'Apple M4 Max',
    accel: 'apple-silicon',
    appleChipId: 'm4-max',
    ramGb: 64,
    vramGb: 58,
    storageFreeGb: 800,
    installedRuntimes: ['ollama', 'lm-studio', 'mlx', 'llama-cpp'],
    preferredRuntime: 'mlx',
  },
  {
    id: 'rtx-4090-desktop-64',
    nickname: 'Desktop — RTX 4090 + 64GB',
    os: 'linux',
    cpuLabel: 'Ryzen 7950X3D',
    accel: 'cuda',
    gpuId: 'rtx-4090',
    ramGb: 64,
    vramGb: 24,
    storageFreeGb: 1200,
    installedRuntimes: ['ollama', 'llama-cpp', 'vllm'],
    preferredRuntime: 'llama-cpp',
  },
  {
    id: 'rtx-3060-12-pc',
    nickname: 'Gaming PC — RTX 3060 12GB',
    os: 'windows',
    cpuLabel: 'Ryzen 5 5600X',
    accel: 'cuda',
    gpuId: 'rtx-3060-12',
    ramGb: 32,
    vramGb: 12,
    storageFreeGb: 400,
    installedRuntimes: ['ollama', 'lm-studio'],
    preferredRuntime: 'lm-studio',
  },
  {
    id: 'mac-mini-m4-24',
    nickname: 'Mac mini M4 24GB',
    os: 'macos',
    cpuLabel: 'Apple M4',
    accel: 'apple-silicon',
    appleChipId: 'm4',
    ramGb: 24,
    vramGb: 20,
    storageFreeGb: 400,
    installedRuntimes: ['ollama'],
    preferredRuntime: 'ollama',
  },
];

export const BENCHMARK_PROFILES: [BenchmarkProfile, ...BenchmarkProfile[]] = [
  {
    id: 'quick-coding',
    label: 'Quick Coding',
    description: '3 short coding prompts, 2 iterations each. Good first run.',
    tasks: ['coding'],
    iterations: 2,
  },
  {
    id: 'coding-plus-review',
    label: 'Coding + Review',
    description: 'Coding, code review, and debugging — balanced for agent workflows.',
    tasks: ['coding', 'review', 'debugging'],
    iterations: 2,
  },
  {
    id: 'full-agent',
    label: 'Full Agent Suite',
    description: 'All six task categories including tool-use reliability.',
    tasks: ['coding', 'review', 'debugging', 'reasoning', 'docs', 'tool-use'],
    iterations: 3,
  },
];

export function findModel(id: string): ModelProfile | undefined {
  return MODELS.find((m) => m.id === id);
}

export function findAppleChip(id: string): AppleChip | undefined {
  return APPLE_CHIPS.find((c) => c.id === id);
}

export function findGpu(id: string): GpuProfile | undefined {
  return GPUS.find((g) => g.id === id);
}

export function findRuntime(id: string): RuntimeProfile | undefined {
  return RUNTIMES.find((r) => r.id === id);
}

export function findMachine(id: string): MachineProfile | undefined {
  return MACHINE_PRESETS.find((m) => m.id === id);
}
